// Загрузка и удаление онлайн-чата amoCRM (CRM Plugin, скрипт gso.amocrm.ru/js/button.js).
//
// button.js глобальный: на странице одновременно живёт один экземпляр, настройки он читает из window
// в момент выполнения. Поэтому:
//  - загрузка и удаление выполняются строго по очереди (queue): новый скрипт не вставляется, пока
//    предыдущий не выполнился, иначе старый скрипт подхватит глобальные объекты нового и чатов станет два;
//  - удалить может только тот, кто загрузил (owner): при перемонтировании layout удаление старого
//    чата не должно снести уже загруженный новый.
//
// Что установлено по коду button.js:
//  - конфиг берётся из window.amoSocialButtonConfig || window.crmPluginConfig, настройки чата — из .onlinechat;
//  - личность в чате — onlinechat.user_id. Без него скрипт создаёт анонимную cookie amo-livechat-id на год;
//  - параметры setMeta, накопленные до загрузки, отправляются при старте;
//  - повторное выполнение скрипта создаёт новый экземпляр, он ищет свой тег по id amo_social_button_script;
//  - runDestroy удаляет кнопку, обработчики и глобальные объекты, но НЕ удаляет класс на <html>
//    (на мобильных при открытом чате он скрывает всю страницу) и cookie.

import projectConfig from '@/projectConfig';

const SCRIPT_ID = 'amo_social_button_script';
const SCRIPT_SRC = 'https://gso.amocrm.ru/js/button.js';
const GLOBALS = ['amoSocialButton', 'crmPlugin', 'amo_social_button', 'crm_plugin', 'amoSocialButtonConfig', 'crmPluginConfig'];

let queue = Promise.resolve();
let currentOwner = null;

function enqueue(task) {
  queue = queue.then(task).catch((error) => console.error('amoChat:', error));
}

function injectScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`не удалось загрузить ${SCRIPT_SRC}`));
    document.head.appendChild(script);
  });
}

function removeChat() {
  const command = window.amoSocialButton || window.crmPlugin;
  if (typeof command === 'function') {
    try {
      command('runChatHide');
      command('runDestroy');
    } catch (error) {
      console.error('amoChat: runDestroy', error);
    }
  }

  GLOBALS.forEach((name) => {
    try {
      delete window[name];
    } catch {
      window[name] = undefined;
    }
  });

  document.getElementById(SCRIPT_ID)?.remove();

  // остатки, которые runDestroy не убирает
  document.querySelectorAll('.amo-button-holder, .amo-livechat, #amo-modal').forEach((el) => el.remove());
  [...document.documentElement.classList]
    .filter((name) => name.startsWith('amo-livechat'))
    .forEach((name) => document.documentElement.classList.remove(name));
  document.cookie = 'amo-livechat-id=; max-age=0; path=/';
}

/**
 * Загружает чат. Возвращает owner — его нужно передать в destroyAmoChat.
 * onlinechat — { user_id, profile }, meta — данные для setMeta.
 * onReady — чат готов (onChatReady), onError — скрипт не загрузился.
 */
export function loadAmoChat({ onlinechat, meta, onReady, onError }) {
  const owner = {};

  enqueue(async () => {
    // последняя загрузка побеждает: если прошлый чат не удалили, удаляем его сами
    if (currentOwner) removeChat();
    currentOwner = owner;

    const { buttonId, buttonHash, locale } = projectConfig.amoChat;

    // то же, что делает код кнопки из amoCRM (вкладка «Установить»), но без inline-скрипта
    window.crmPluginConfig = { onlinechat };
    window.amo_social_button = {
      id: buttonId,
      hash: buttonHash,
      locale,
      inline: false,
      setMeta(params) {
        this.params = (this.params || []).concat([params]);
      }
    };
    window.amoSocialButton = function () {
      (window.amoSocialButton.q = window.amoSocialButton.q || []).push(arguments);
    };

    window.amo_social_button.setMeta(meta);
    window.amoSocialButton('onChatReady', () => {
      if (currentOwner === owner) onReady?.();
    });

    try {
      await injectScript();
    } catch (error) {
      console.error('amoChat:', error);
      if (currentOwner === owner) onError?.(error);
    }
  });

  return owner;
}

export function destroyAmoChat(owner) {
  enqueue(() => {
    if (!owner || currentOwner !== owner) return;
    currentOwner = null;
    removeChat();
  });
}
